import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { focusMainContent } from '../../../utils/focus-main-content';

const RETRY_DELAY = 100;
const HEADING_ATTEMPTS = 5;
const MAX_ATTEMPTS = 20;

const isRegistrationPath = (pathname: string) => {
	return /\/registration(\/|$)/.test(pathname);
};

export const useFocusMainOnNavigation = (): void => {
	const { pathname } = useLocation();
	const previousPathname = useRef(pathname);
	const renderedPathname = useRef(pathname);
	const navigatedAt = useRef(0);
	const lastInteractionAt = useRef(0);

	if (renderedPathname.current !== pathname) {
		renderedPathname.current = pathname;
		navigatedAt.current = performance.now();
	}

	useEffect(() => {
		const recordInteraction = () => {
			lastInteractionAt.current = performance.now();
		};

		document.addEventListener('keydown', recordInteraction, true);
		document.addEventListener('pointerdown', recordInteraction, true);
		return () => {
			document.removeEventListener('keydown', recordInteraction, true);
			document.removeEventListener('pointerdown', recordInteraction, true);
		};
	}, []);

	useEffect(() => {
		const previous = previousPathname.current;
		if (previous === pathname) return;

		previousPathname.current = pathname;
		if (!lastInteractionAt.current) return;
		if (isRegistrationPath(previous) || isRegistrationPath(pathname)) return;

		let attempts = 0;
		let timeout: ReturnType<typeof setTimeout>;
		const tryFocus = () => {
			const userMovedFocus = lastInteractionAt.current > navigatedAt.current && document.activeElement !== document.body;
			if (userMovedFocus) return;

			attempts += 1;
			if (focusMainContent(attempts > HEADING_ATTEMPTS) || attempts >= MAX_ATTEMPTS) return;
			timeout = setTimeout(tryFocus, RETRY_DELAY);
		};

		timeout = setTimeout(tryFocus, RETRY_DELAY);
		return () => {
			clearTimeout(timeout);
		};
	}, [pathname]);
};
